import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 } from "uuid";

import { dayjs } from "@thalamos/common";
import type {
  PatientStateWithContext,
  ResolvedSection136PlaceOfSafety,
} from "@thalamos/aspire-common";
import {
  calculateNhsNumberIndicatorStatus,
  isGuestUserSession,
  isMcaUserSession,
  MhaStatus,
  selectSection136BannerStates,
} from "@thalamos/aspire-common";

import { LoggedInUserContext } from "../../../Contexts.js";
import { useScreenDetection } from "../../../hooks/ScreenDetection/useScreenDetection.js";
import { patientLandingRoute } from "../../../pages/CareHomeRegister/patientLandingRoute.js";
import { PatientUndoMergeDialog } from "../../../pages/PatientIndex/PatientMerge/PatientUndoMergeDialog.js";
import { routeFns } from "../../../routePaths.js";
import { MenuFlyout } from "../../design-system/MenuFlyout.js";
import { HorizontalLine } from "../../form/HorizontalLine.js";
import { PatientCrisisInfo } from "../PatientCrisisInfo.js";
import { StickyContainer } from "../styleWrappers.js";
import { DualMhaStatusDisplay } from "./DualMhaStatusDisplay.js";
import {
  RenderDateOfBirth,
  RenderGpPractice,
  RenderMhaStatusInfoBox,
  RenderNhsNumber,
  RenderPatientAddress,
  RenderPatientAge,
  RenderPatientName,
  RenderPostCode,
  shouldShowNhsLabel,
  Spacer,
  type PatientBannerProps,
} from "./PatientBannerParts.js";
import {
  PatientStateDisplay,
  shouldResolveSection136PlaceOfSafety,
} from "./PatientStateDisplay.js";
import { RafIndicator } from "./RafPopout.js";
import { api } from "~/api.js";
import type { ExternalPatientLink } from "~/components/design-system/ExternalPatientLink/externalPatientLink.js";
import {
  computeSyncBadgeColour,
  filterPullableLinks,
} from "~/components/design-system/ExternalPatientLink/PatientExternalLinkActions/helpers.js";
import { PatientExternalLinkActions } from "~/components/design-system/ExternalPatientLink/PatientExternalLinkActions/PatientExternalLinkActions.js";
import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import { IndicatorStatus } from "~/components/design-system/IndicatorStatus/IndicatorStatus.js";
import { S132BannerStatus } from "~/components/s132/S132BannerStatus.js";
import { isValidPatientDemographics } from "~/components/util.js";
import { useMhaStatus, useSection136PlaceOfSafety } from "~/hooks/apiCalls.js";
import { useExternalPatientLinks } from "~/hooks/ExternalPatientLink/useExternalPatientLinks.js";
import { useOdsOrganisation } from "~/hooks/useOdsOrganisation.js";
import { userCanEditMhaStatus } from "~/pages/EditMhaStatus/userCanEditMhaStatus.js";
import { PatientAutoMergeChecker } from "~/pages/PatientMergeV2/PatientAutoMergeChecker.js";

export interface PatientBannerInnerProps extends PatientBannerProps {
  showNhsLabel: boolean;
  isEditable: boolean;
  patientMhaStatusEnabled: boolean;
  nhsNumberIndicatorEnabled: boolean;
  showEditMhaStatus: boolean;
  hasPatientMergeAccess: boolean;
  isGuestUserSession: boolean;
  mhaStatus:
    | {
        states: PatientStateWithContext[];
      }
    | undefined;
  reloadMhaStatus?: () => void;
  resolvedSection136PlaceOfSafety?: ResolvedSection136PlaceOfSafety;
}

export interface PatientBannerInnerRenderProps extends PatientBannerInnerProps {
  externalLinks: ExternalPatientLink[];
  onNavigate: (to: string) => void;
  currentPathname: string;
}

function SuggestedMergePopup({
  alertId,
  patientId,
}: {
  alertId: string;
  patientId: string;
}) {
  const [open, setOpen] = useState(true);

  const onClose = useCallback(() => {
    api.patients.markAlertAsSeen(patientId, alertId);
    setOpen(false);
  }, [alertId, patientId]);

  return (
    <PopupDialog open={open} onClose={onClose}>
      <PopupDialogTitle
        titleText={`Patient local record is wrongly identified`}
        closeDialog={onClose}
      />
      <Typography>
        The NHS number on this record has been replaced in NHS national systems.
        The patient should be told of the replacement NHS number. The new NHS
        number is already linked to an existing patient record within eMHA -
        your local back office have been notified and will merge the records
        together.
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: "1em",
        }}
      >
        <Button variant="outlined" label={"Close"} onClick={onClose} />
      </Box>
    </PopupDialog>
  );
}

function InvalidatedPatientPopup({
  alertId,
  patientId,
}: {
  alertId: string;
  patientId: string;
}) {
  const [open, setOpen] = useState(true);

  const onClose = useCallback(() => {
    api.patients.markAlertAsSeen(patientId, alertId);
    setOpen(false);
  }, [alertId, patientId]);

  return (
    <PopupDialog open={open} onClose={onClose}>
      <PopupDialogTitle
        titleText={`NHS number is no longer valid`}
        closeDialog={onClose}
      />
      <Typography>
        This local record is wrongly identified and the NHS number that was on
        this record is no longer valid. The record has been decoupled from PDS
        and has been referred to the local back office for processing.
      </Typography>
      <Banner
        sx={{ marginTop: "1em" }}
        bannerType={BannerList.WARNING}
        title="All demographic and clinical
        information for this patient should be treated with caution until the
        processing is complete as there are data anomalies on the record which
        could constitute a clinical risk. The anomalies might be related to the
        clinical as well as the demographic record."
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: "1em",
        }}
      >
        <Button variant="outlined" label={"Close"} onClick={onClose} />
      </Box>
    </PopupDialog>
  );
}

function DeceasedPatientPopup({
  alertId,
  patientId,
}: {
  alertId: string;
  patientId: string;
}) {
  const [open, setOpen] = useState(true);

  const { t } = useTranslation();

  const onClose = useCallback(() => {
    api.patients.markAlertAsSeen(patientId, alertId);
    setOpen(false);
  }, [alertId, patientId]);

  return (
    <PopupDialog open={open} onClose={onClose}>
      <PopupDialogTitle
        titleText={t("components.deceasedModal.title")}
        closeDialog={onClose}
      />
      <Typography>{t("components.deceasedModal.message")}</Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: "1em",
        }}
      >
        <Button variant="outlined" label={"Close"} onClick={onClose} />
      </Box>
    </PopupDialog>
  );
}

export function PatientBanner(props: PatientBannerProps) {
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user;
  const nhsNumberEnabled =
    user?.sessionOrganisationConfiguration?.nhsNumberEnabled ?? false;

  const isEditable = !isGuestUserSession(user);

  // An MCA team's capacity position lives on the resident page. A detention status is
  // not theirs to act on, and showing it beside a capacity position invites the reader to
  // treat one as bearing on the other, so it is withheld rather than replaced here. Not
  // configurable: it follows from what an MCA team is, not from local preference.
  const patientMhaStatusEnabled =
    (user?.sessionOrganisationConfiguration?.patientStateEnabled ?? false) &&
    !isMcaUserSession(user);

  const nhsNumberIndicatorEnabled =
    user?.sessionOrganisationConfiguration?.pdsBatchTracingEnabled ?? false;

  const showEditMhaStatus = userCanEditMhaStatus(user);

  const hasPatientMergeAccess = user?.hasPatientMergeAccess ?? false;

  const { mhaStatus, reloadMhaStatus } = useMhaStatus({
    patientId: props.patient.id,
  });

  const resolveSection136PlaceOfSafety = shouldResolveSection136PlaceOfSafety(
    user?.sessionOrganisationConfiguration?.patientStateEnabled ?? false,
    mhaStatus?.states,
  );
  const { section136PlaceOfSafety: resolvedSection136PlaceOfSafety } =
    useSection136PlaceOfSafety({
      patientId: props.patient.id,
      skip: !resolveSection136PlaceOfSafety,
    });

  // The NHS label is displayed if the NHS number feature is turned on.
  // If the feature is turned off, the label will still be displayed if the patient already has an NHS number recorded.
  const showNhsLabel = shouldShowNhsLabel(nhsNumberEnabled, props.nhsNumber);

  const guestUserSession = isGuestUserSession(user);

  return (
    <PatientBannerInner
      {...props}
      nhsNumberIndicatorEnabled={nhsNumberIndicatorEnabled}
      showNhsLabel={showNhsLabel}
      isEditable={isEditable}
      patientMhaStatusEnabled={patientMhaStatusEnabled}
      showEditMhaStatus={showEditMhaStatus}
      hasPatientMergeAccess={hasPatientMergeAccess}
      isGuestUserSession={guestUserSession}
      mhaStatus={mhaStatus}
      reloadMhaStatus={reloadMhaStatus}
      resolvedSection136PlaceOfSafety={resolvedSection136PlaceOfSafety}
    />
  );
}

export function PatientBannerInner(props: PatientBannerInnerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { externalPatientLinks } = useExternalPatientLinks({
    patientId: props.patient.id,
  });

  useEffect(() => {
    if (!isValidPatientDemographics(props.patient)) {
      navigate(routeFns.patientEdit(props.patient.id, location.pathname));
    }
  }, [props.patient, navigate, location.pathname]);

  return (
    <PatientBannerInnerRender
      {...props}
      externalLinks={externalPatientLinks?.externalLinks ?? []}
      onNavigate={(to) => {
        navigate(to);
      }}
      currentPathname={location.pathname}
    />
  );
}

export function PatientBannerInnerRender(props: PatientBannerInnerRenderProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const userContext = useContext(LoggedInUserContext);
  const { isMobileView } = useScreenDetection();
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);

  const [showPatientUndoMergeDialog, setShowPatientUndoMergeDialog] =
    useState(false);

  const externalLinks = props.externalLinks;

  const mainTextStyles = {
    color: "text.primary",
    fontSize: isMobileView ? theme.spacing(1.5) : theme.spacing(2),
  };

  const isHeaderSticky = !props.disableSticky;
  const bannerPadding = isMobileView ? theme.spacing(1.5) : theme.spacing(2.5);
  const bannerBorderRadius = theme.spacing(0.8);
  const bodyOverlapOffset = bannerPadding;
  const [mobileStickyTopOffset, setMobileStickyTopOffset] = useState(0);
  const isDeceased = ["formal", "informal"].includes(
    props.patient.deathNotificationStatus ?? "",
  );
  const bannerBorder = props.patient.restricted
    ? "1px solid red"
    : isDeceased
      ? "1px solid black"
      : undefined;

  const patientAge = dayjs().diff(dayjs(props.patient.dateOfBirth), "year");

  const gpPracticeDisplayEnabled =
    userContext?.user?.sessionOrganisationConfiguration
      ?.gpPracticeDisplayEnabled ?? false;

  const { organisation: gpOrganisation, loading: gpLoading } =
    useOdsOrganisation(
      gpPracticeDisplayEnabled ? props.patient.registeredGpOdsCode : null,
    );

  const showExtendedContent = props.disableSticky || !isMobileView;
  const showMobileMhaStatus =
    !showExtendedContent && props.patientMhaStatusEnabled;

  const cardRef = useRef(null);

  const menuFlyoutOptions = [];

  const disableEditMHAStatusBtn =
    props.currentPathname === routeFns.editMhaStatusPage(props.patient.id) ||
    props.currentPathname.endsWith("confirm-mha-status-finalise");

  // If we are already on the patient merge popup, we should disable the merge and unmerge buttons
  const disablePatientMergeAndUnmergeBtn =
    props.currentPathname === routeFns.patientMergeSearch(props.patient.id);

  const patientRoute = patientLandingRoute(userContext?.user, props.patient.id);

  if (props.currentPathname !== patientRoute) {
    menuFlyoutOptions.push({
      icon: "view",
      name: t("layout.patientBanner.link"),
      link: patientRoute,
      disabled: false,
    });
  }

  if (props.isEditable) {
    menuFlyoutOptions.push({
      icon: "person",
      name: t("layout.patientBanner.edit"),
      link: routeFns.patientEdit(props.patient.id),
      disabled: false,
    });
  }

  if (props.hasPatientMergeAccess && !disablePatientMergeAndUnmergeBtn) {
    menuFlyoutOptions.push({
      icon: "merge",
      name: t("layout.patientBanner.merge"),
      onClick: () => {
        props.onNavigate(routeFns.patientMergeSearch(props.patient.id));
      },
      disabled: false,
    });
  }

  const mostRecentlyMergedPatient =
    props.patientTimeline?.mergedPatientData.lastMergedPatient;

  // we should only show the undo merge option if the merged patient
  // was last merged more than 30 days ago.
  const isMostRecentlyMergedBefore30Days = dayjs(
    mostRecentlyMergedPatient?.lastMerged,
  ).isBefore(dayjs().add(30, "day"));

  if (
    mostRecentlyMergedPatient &&
    isMostRecentlyMergedBefore30Days &&
    props.hasPatientMergeAccess &&
    !disablePatientMergeAndUnmergeBtn
  ) {
    menuFlyoutOptions.push({
      icon: "undo",
      name: t("layout.patientBanner.undoMerge"),
      onClick: () => {
        setShowPatientUndoMergeDialog(true);
      },
      disabled: false,
    });
  }

  if (isMobileView && !disableEditMHAStatusBtn && props.showEditMhaStatus) {
    menuFlyoutOptions.push({
      icon: "edit",
      name: t("pages.editMHAStatus.editMHAStatus"),
      link: routeFns.editMhaStatusPage(props.patient.id),
      disabled: false,
    });
  }

  const superesedMergeAlertToShow =
    props.patientTimeline?.demographicsAlertsToShow.find(
      (alert) =>
        alert.type === "suggested-merge" &&
        alert.suggestedMerge.type === "superseded",
    );

  const invalidatedPatientAlertToShow =
    props.patientTimeline?.demographicsAlertsToShow.find(
      (alert) => alert.type === "alert" && alert.alert.type === "invalidated",
    );

  const deceasedPatientAlertToShow =
    props.patientTimeline?.demographicsAlertsToShow.find(
      (alert) => alert.type === "alert" && alert.alert.type === "deceased",
    );

  const isAutoMergeCheckEnabled =
    !props.disableAutoMergeCheck &&
    userContext?.user?.sessionOrganisationConfiguration
      ?.patientAutoMergeEnabled;

  const isRioEnabled =
    (
      userContext?.user.sessionOrganisationConfiguration?.rioInstancesEnabled ??
      []
    ).length > 0;

  const isPdsEnabled =
    userContext?.user.sessionOrganisationConfiguration?.pdsEnabled &&
    userContext.user.sessionOrganisationConfiguration
      .pdsEndUserOrganisationODSCode;

  const hasInvalidLink = // There is no invalid link for this NHS number
    externalLinks.some(
      (l) =>
        props.nhsNumber &&
        l.isInvalid &&
        l.externalPatientIdValue.includes(props.nhsNumber),
    );

  const shouldShowCreateRioLinkButton =
    props.showNhsLabel &&
    // We have an NHS Number
    props.nhsNumber &&
    // There is no invalid link for this NHS number
    !hasInvalidLink &&
    // We have a Rio instance
    isRioEnabled &&
    // There are some existing external patient links
    externalLinks.length > 0 &&
    // There is no invalid link for this NHS number
    !externalLinks.find(
      (l) =>
        props.nhsNumber &&
        l.isInvalid &&
        l.externalPatientIdValue.includes(props.nhsNumber),
    ) &&
    // None of the external patient links to rio
    !externalLinks.find((l) => l.externalSystemType === "rio" && l.canPull);

  const shouldShowLinkToPdsButton =
    // We are feature switched to show NHS Numbers
    props.patientTimeline?.patient &&
    props.showNhsLabel &&
    // We have an NHS Number
    props.nhsNumber &&
    // There is no invalid link for this NHS number
    !hasInvalidLink &&
    // We have a PDS instance
    isPdsEnabled &&
    // There are some existing external patient links
    externalLinks.length > 0 &&
    // None of the external patient links are to our system
    !externalLinks.find((l) => l.externalSystemType === "pds");

  const mobilePullableLinks =
    isMobileView && !props.isHidden
      ? filterPullableLinks(
          externalLinks,
          !!shouldShowCreateRioLinkButton,
          !!shouldShowLinkToPdsButton,
        )
      : [];

  const hasMobileExternalLinkItems =
    mobilePullableLinks.length > 0 ||
    !!shouldShowCreateRioLinkButton ||
    !!shouldShowLinkToPdsButton;

  const mobileSyncBadgeColour =
    isMobileView && !props.isHidden
      ? computeSyncBadgeColour(mobilePullableLinks)
      : undefined;

  const indicatorStatus = props.nhsNumberIndicatorEnabled
    ? calculateNhsNumberIndicatorStatus(
        externalLinks,
        props.patientTimeline?.patient.suggestedMerges ?? [],
      )
    : null;

  useEffect(() => {
    if (!isMobileView) {
      setMobileStickyTopOffset(0);
      return;
    }

    const updateMobileStickyTopOffset = () => {
      const mobileMenu = document.querySelector<HTMLElement>(
        '[data-testid="mobile-menu"]',
      );

      setMobileStickyTopOffset(mobileMenu?.offsetHeight ?? 0);
    };

    updateMobileStickyTopOffset();
    window.addEventListener("resize", updateMobileStickyTopOffset);

    return () => {
      window.removeEventListener("resize", updateMobileStickyTopOffset);
    };
  }, [isMobileView]);

  const stickyTopOffset = isMobileView
    ? mobileStickyTopOffset
    : props.isGuestUserSession
      ? 30
      : 0;

  return (
    <>
      {superesedMergeAlertToShow && (
        <SuggestedMergePopup
          alertId={superesedMergeAlertToShow.id}
          patientId={props.patient.id}
        />
      )}
      {invalidatedPatientAlertToShow && (
        <InvalidatedPatientPopup
          alertId={invalidatedPatientAlertToShow.id}
          patientId={props.patient.id}
        />
      )}
      {deceasedPatientAlertToShow && (
        <DeceasedPatientPopup
          alertId={deceasedPatientAlertToShow.id}
          patientId={props.patient.id}
        />
      )}
      {isAutoMergeCheckEnabled && props.patientTimeline && (
        <PatientAutoMergeChecker patientTimeline={props.patientTimeline} />
      )}
      <RenderUndoMergeDialog
        showPatientUndoMergeDialog={showPatientUndoMergeDialog}
        setShowPatientUndoMergeDialog={setShowPatientUndoMergeDialog}
        reloadPatientTimeline={props.reloadPatientTimeline}
        reloadMhaStatus={props.reloadMhaStatus}
      />
      <StickyContainer
        data-testid="patient-banner"
        disableSticky={props.disableSticky ? "true" : undefined}
        issticky={isHeaderSticky ? "true" : undefined}
        ismobilemode={isMobileView ? "true" : undefined}
        top={stickyTopOffset}
        style={{ zIndex: 999, backgroundColor: "transparent" }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.secondary.light,
            borderTopLeftRadius: bannerBorderRadius,
            borderTopRightRadius: bannerBorderRadius,
            ...(showExtendedContent || showMobileMhaStatus
              ? {}
              : {
                  borderBottomLeftRadius: bannerBorderRadius,
                  borderBottomRightRadius: bannerBorderRadius,
                }),
            ...(bannerBorder
              ? {
                  borderTop: bannerBorder,
                  borderLeft: bannerBorder,
                  borderRight: bannerBorder,
                  ...(showExtendedContent || showMobileMhaStatus
                    ? {}
                    : { borderBottom: bannerBorder }),
                }
              : {}),
            padding: bannerPadding,
            paddingBottom: bannerPadding,
          }}
        >
          <Box
            onClick={() => {
              setIsMainMenuOpen(true);
            }}
            sx={{
              display: "flex",

              "&:hover": {
                cursor: "pointer",
                "& .MuiIconButton-root": {
                  backgroundColor: theme.palette.secondary.lightBlue,
                },
              },
            }}
          >
            <Box>
              <RenderPatientName
                deceased={isDeceased}
                restricted={props.patient.restricted}
                familyName={props.patient.name.family}
                givenName={props.patient.name.given}
                styleType={mainTextStyles}
                fontSize={18}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                marginLeft: "auto",
              }}
            >
              {props.showNhsLabel && (
                <>
                  <RenderNhsNumber
                    nhsNumber={props.nhsNumber}
                    onReconcileClick={props.onReconcileClick}
                  />

                  {indicatorStatus && (
                    <IndicatorStatus
                      status={indicatorStatus.value}
                      systemName={
                        indicatorStatus.value === "02" &&
                        "link" in indicatorStatus
                          ? indicatorStatus.link.externalSystemDisplayName
                          : undefined
                      }
                    />
                  )}

                  <RafIndicator
                    patientId={props.patient.id}
                    nhsNumber={props.nhsNumber}
                  />
                </>
              )}
              {!props.isHidden && (
                <Box
                  ref={cardRef}
                  sx={{
                    display: "flex",
                    marginLeft: (theme) => theme.spacing(5.2),
                    alignItems: "center",
                  }}
                  onClick={() => {
                    setIsMainMenuOpen(true);
                  }}
                >
                  {(!!menuFlyoutOptions.length ||
                    (isMobileView &&
                      !props.isHidden &&
                      hasMobileExternalLinkItems)) && (
                    <MenuFlyout
                      cardRef={cardRef}
                      options={menuFlyoutOptions}
                      isOpen={isMainMenuOpen}
                      onClose={() => {
                        setIsMainMenuOpen(false);
                      }}
                      badgeColour={mobileSyncBadgeColour}
                      customItems={
                        isMobileView &&
                        !props.isHidden &&
                        hasMobileExternalLinkItems ? (
                          <PatientExternalLinkActions
                            patient={props.patientTimeline?.patient}
                            externalLinks={externalLinks}
                            shouldShowCreateRioLinkButton={
                              !!shouldShowCreateRioLinkButton
                            }
                            shouldShowLinkToPdsButton={
                              !!shouldShowLinkToPdsButton
                            }
                            displayMode="mobile"
                          />
                        ) : undefined
                      }
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </StickyContainer>

      {showMobileMhaStatus && (
        <Box
          sx={{
            backgroundColor: theme.palette.secondary.light,
            borderBottomLeftRadius: bannerBorderRadius,
            borderBottomRightRadius: bannerBorderRadius,
            ...(bannerBorder
              ? {
                  borderBottom: bannerBorder,
                  borderLeft: bannerBorder,
                  borderRight: bannerBorder,
                }
              : {}),
            marginTop: `-${bodyOverlapOffset}`,
          }}
        >
          <Box
            sx={{
              px: bannerPadding,
              pt: `calc(${bodyOverlapOffset} + 0.5em)`,
              pb: bannerPadding,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <HorizontalLine noMargin={true} />
            </Box>
            <Box sx={{ marginTop: "1em" }}>
              <RenderMhaStatusInfoBox
                mhaStatus={props.mhaStatus}
                showUnknownMhaStatus={props.showUnknownMhaStatus}
              />
            </Box>
          </Box>
        </Box>
      )}

      {showExtendedContent && (
        <Box
          sx={{
            backgroundColor: theme.palette.secondary.light,
            borderBottomLeftRadius: bannerBorderRadius,
            borderBottomRightRadius: bannerBorderRadius,
            ...(bannerBorder
              ? {
                  borderBottom: bannerBorder,
                  borderLeft: bannerBorder,
                  borderRight: bannerBorder,
                }
              : {}),
            marginTop: `-${bodyOverlapOffset}`,
          }}
        >
          <Box
            sx={{
              px: bannerPadding,
              pt: `calc(${bodyOverlapOffset} + 0.5em)`,
              pb: bannerPadding,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <HorizontalLine noMargin={true} />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                marginTop: "0.5em",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  width: "100%",
                }}
              >
                <RenderPatientAge
                  patientAge={
                    patientAge ? patientAge : t("layout.patientBanner.unknown")
                  }
                  styleType={mainTextStyles}
                />

                <Spacer />

                <RenderPatientAddress
                  address={props.patient.address.address}
                  styleType={mainTextStyles}
                />

                <Spacer />
                <RenderPostCode
                  postalCode={props.patient.address.postalCode}
                  styleType={mainTextStyles}
                />

                <Spacer />

                <RenderDateOfBirth
                  dateOfBirth={
                    props.patient.dateOfBirth
                      ? dayjs(props.patient.dateOfBirth).format("DD MMM YYYY")
                      : t("layout.patientBanner.unknown")
                  }
                  styleType={mainTextStyles}
                />

                {gpPracticeDisplayEnabled &&
                  props.patient.registeredGpOdsCode && (
                    <>
                      <Spacer />
                      <RenderGpPractice
                        odsCode={props.patient.registeredGpOdsCode}
                        organisationName={gpOrganisation?.name ?? null}
                        loading={gpLoading}
                        styleType={mainTextStyles}
                      />
                    </>
                  )}

                <Spacer />
              </Box>
              {!props.isHidden && !isMobileView && (
                <PatientExternalLinkActions
                  patient={props.patientTimeline?.patient}
                  externalLinks={externalLinks}
                  shouldShowCreateRioLinkButton={
                    !!shouldShowCreateRioLinkButton
                  }
                  shouldShowLinkToPdsButton={!!shouldShowLinkToPdsButton}
                />
              )}
            </Box>
            {props.patientMhaStatusEnabled && (
              <>
                <Box sx={{ marginTop: "0.5em", width: "100%" }}>
                  <HorizontalLine noMargin={true} />
                </Box>
                <Box
                  sx={{
                    marginTop: "1em",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "stretch",
                    gap: 2,
                  }}
                >
                  {isMobileView ? (
                    <RenderMhaStatusInfoBox
                      mhaStatus={props.mhaStatus}
                      showUnknownMhaStatus={props.showUnknownMhaStatus}
                    />
                  ) : (
                    (() => {
                      const states = props.mhaStatus?.states ?? [];
                      const unknownState = {
                        id: v4(),
                        isLatest: true,
                        effectiveDateTime: dayjs().toISOString(),
                        patientId: props.patient.id,
                        state: {
                          mhaStatus: { status: MhaStatus.Unknown },
                        },
                      };
                      // Dual-status display (§6.5): see
                      // `selectSection136BannerStates` for the rules.
                      const selection = selectSection136BannerStates(states);
                      if (selection.kind === "dual") {
                        // Fixed mapping: the non-136 detention is the
                        // "Existing MHA Status" (and the editable side); the
                        // Section 136 is the "Coexisting MHA Status".
                        return (
                          <DualMhaStatusDisplay
                            existingState={selection.other}
                            coexistingState={selection.section136}
                            disableEditMHAStatusBtn={disableEditMHAStatusBtn}
                            showEditMhaStatus={props.showEditMhaStatus}
                            resolvedSection136PlaceOfSafety={
                              props.resolvedSection136PlaceOfSafety
                            }
                            onEditMhaStatus={() => {
                              props.onNavigate(
                                routeFns.editMhaStatusPage(props.patient.id),
                              );
                            }}
                          />
                        );
                      }
                      return (
                        <PatientStateDisplay
                          state={selection.state ?? unknownState}
                          disableEditMHAStatusBtn={disableEditMHAStatusBtn}
                          showEditMhaStatus={props.showEditMhaStatus}
                          resolvedSection136PlaceOfSafety={
                            props.resolvedSection136PlaceOfSafety
                          }
                          onEditMhaStatus={() => {
                            props.onNavigate(
                              routeFns.editMhaStatusPage(props.patient.id),
                            );
                          }}
                        />
                      );
                    })()
                  )}
                  <S132BannerStatus
                    patientId={props.patient.id}
                    patientName={`${props.patient.name.family.toUpperCase()}, ${props.patient.name.given}`}
                  />
                </Box>
              </>
            )}
          </Box>
          {!props.isHidden && <PatientCrisisInfo />}
        </Box>
      )}
      {!props.isHorizontalLineHidden && (
        <Box sx={{ my: isMobileView ? 2 : 3 }}>{/*<HorizontalLine />*/}</Box>
      )}
    </>
  );
}

function RenderUndoMergeDialog({
  showPatientUndoMergeDialog,
  setShowPatientUndoMergeDialog,
  reloadPatientTimeline,
  reloadMhaStatus,
}: {
  showPatientUndoMergeDialog: boolean;
  setShowPatientUndoMergeDialog: (show: boolean) => void;
  reloadPatientTimeline?: () => void;
  reloadMhaStatus?: () => void;
}) {
  return (
    <>
      {showPatientUndoMergeDialog && (
        <PatientUndoMergeDialog
          closeUndoMergeDialog={() => {
            setShowPatientUndoMergeDialog(false);
          }}
          reloadPatientTimeline={reloadPatientTimeline}
          reloadMhaStatus={reloadMhaStatus}
        />
      )}
    </>
  );
}
